import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { pageHref } from "../../lib/format";
import { AdminHeader, Badge, Field, Panel, Row, Table, borderStyle, inputCls } from "../ui";
import { PageLoader } from "../../components/States";

export default function PagesAdmin() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ slug: "", title: "" });

  const load = useCallback(() => {
    adminApi
      .get("/admin/pages", { params: { search: q || undefined } })
      .then((r) => setRows(r.data.pages))
      .catch(() => setRows([]));
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  async function create(e) {
    e.preventDefault();
    try {
      await adminApi.post("/admin/pages", draft);
      toast.success("Page created");
      setCreating(false);
      setDraft({ slug: "", title: "" });
      load();
    } catch (err) {
      toast.error("Could not create page", { description: err?.response?.data?.detail });
    }
  }

  async function remove(slug) {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/pages/${slug}`);
      toast.success("Page deleted");
      load();
    } catch (err) {
      toast.error("Could not delete", { description: err?.response?.data?.detail });
    }
  }

  return (
    <div data-testid="admin-pages">
      <AdminHeader title="Pages" subtitle={rows ? `${rows.length} pages` : ""}>
        <div
          className="flex items-center gap-2 rounded-lg border px-3 py-1.5"
          style={borderStyle}
        >
          <Search className="h-4 w-4 gold-text" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages"
            className="bg-transparent text-sm outline-none"
            data-testid="admin-pages-search"
          />
        </div>
        <button type="button" onClick={() => setCreating((v) => !v)} className="btn-gold" data-testid="admin-new-page-button">
          <Plus className="h-4 w-4" /> New page
        </button>
      </AdminHeader>

      {creating && (
        <Panel className="mb-5">
          <form className="grid gap-4 sm:grid-cols-3" onSubmit={create}>
            <Field label="Slug (URL)">
              <input
                value={draft.slug}
                onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))}
                required
                placeholder="new-category"
                className={inputCls}
                style={borderStyle}
                data-testid="new-page-slug"
              />
            </Field>
            <Field label="Title">
              <input
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                required
                className={inputCls}
                style={borderStyle}
                data-testid="new-page-title"
              />
            </Field>
            <div className="flex items-end">
              <button type="submit" className="btn-gold" data-testid="new-page-save">Create</button>
            </div>
          </form>
        </Panel>
      )}

      {!rows ? (
        <PageLoader />
      ) : (
        <Table head={["Title", "Slug", "Type", "Blocks", "Words", "Status", ""]}>
          {rows.map((p) => (
            <Row key={p.id} testid="admin-page-row">
              <td className="px-4 py-3 font-medium">{p.title}</td>
              <td className="px-4 py-3 font-mono text-xs text-[color:var(--nq-stone)]">{p.slug}</td>
              <td className="px-4 py-3">
                {p.is_hub ? <Badge tone="green">hub</Badge> : <Badge>{p.kind}</Badge>}
              </td>
              <td className="px-4 py-3 font-mono text-xs">{p.block_count}</td>
              <td className="px-4 py-3 font-mono text-xs">{p.word_count}</td>
              <td className="px-4 py-3">
                {p.published ? <Badge tone="gold">live</Badge> : <Badge tone="red">draft</Badge>}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Link
                    to={`/admin/pages/edit/${p.slug}`}
                    className="rounded p-1.5 hover:bg-white/10"
                    title="Edit"
                    data-testid="admin-page-edit-link"
                  >
                    <Pencil className="h-4 w-4 gold-text" />
                  </Link>
                  <a
                    href={pageHref(p.slug)}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded p-1.5 hover:bg-white/10"
                    title="View"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(p.slug)}
                    className="rounded p-1.5 hover:bg-white/10"
                    title="Delete"
                    data-testid="admin-page-delete"
                  >
                    <Trash2 className="h-4 w-4" style={{ color: "#e07a72" }} />
                  </button>
                </div>
              </td>
            </Row>
          ))}
        </Table>
      )}
    </div>
  );
}
